import { createSelector } from '@reduxjs/toolkit';
import { ApplicationOverrideTool } from 'types/applicationPath';
import { RootState } from '../store';

// Base selector
export const cityViewSelector = (state: RootState) => state.cityView;

export const cityViewGeneralSelector = (state: RootState) => cityViewSelector(state).general;

export const cityViewSettingsSelector = (state: RootState) => cityViewSelector(state).settings;

export const cityViewGeneralSettingsSelector = createSelector(
  (state: RootState) => cityViewSettingsSelector(state),
  (settings) => ({
    buildingsActive: settings.buildings.active,
    existingEditableBuildingsActive: settings.existingEditableBuildings.active,
    parkingLotsActive: settings.parkingLots.active,
    existingBuildingsActive: settings.existingBuildings.active,
    demolishedBuildingsActive: settings.existingBuildingWireframes.active,
    terrainActive: settings.terrain.active,
    terrainWidth: settings.terrain.width,
    buffersActive: settings.buffers.active,
    restrictionsActive: settings.restrictions.active,
    vegetationActive: settings.vegetation.active,
    mapOverlayActive: settings.mapOverlay.active,
    mapOverlayType: settings.mapOverlay.type,
    snappingActive: settings.snapping.active,
    skyActive: settings.sky.active,
    showFacadeInTerrainTopicActive: settings.showFacadeInTerrainTopic.active,
    gridActive: settings.grid.active,
    gridStep: settings.grid.step,
    gridRotationAngle: settings.grid.rotationAngle,
    offsetTerrainActive: settings.offsetTerrain.active,
    offsetTerrainHeight: settings.offsetTerrain.heightFromTerrain,
    solarExposureActive: settings.solarExposure.active,
    solarExposureType: settings.solarExposure.type,
    solarExposureInterval: settings.solarExposure.interval,
    solarExposureIntervalMonth: settings.solarExposure.intervalMonth,
    clippingActive: settings.clipping.active,
    clippingConstant: settings.clipping.constant,
    clippingAngle: settings.clipping.angle,
  }),
);

export const cityViewCameraTypeSelector = (state: RootState) => cityViewSettingsSelector(state).camera.type;

export const cityViewTerrainWidthSelector = (state: RootState) => cityViewSettingsSelector(state).terrain.width;

export const selectedObjectsSelector = (state: RootState) => state.cityView.selections.selectedObjects;

export const isFacadeSettingsPickerActiveSelector = (state: RootState) =>
  [
    ApplicationOverrideTool.FACADE_WALL_LAYOUT_SETTINGS_PICKER,
    ApplicationOverrideTool.FACADE_MATERIAL_SETTINGS_PICKER,
    ApplicationOverrideTool.FACADE_BALCONY_SETTINGS_PICKER,
  ].includes(cityViewGeneralSelector(state).overrideTool);

export const applicationPathSelector = (state: RootState) => cityViewGeneralSelector(state).applicationPath;

export const addVegetationTypeSelector = (state: RootState) => cityViewSelector(state).landscape.addVegetationType;

export const clippingSettingsSelector = (state: RootState) => cityViewSettingsSelector(state).clipping;

export const showTerrainSelector = (state: RootState) => cityViewSettingsSelector(state).terrain.active;

export const mouseSelector = (state: RootState) => cityViewSelector(state).mouse;

export const isMouseEditingSelector = (state: RootState) => mouseSelector(state).isMouseEditing;

export const isMouseOnGizmoDownSelector = (state: RootState) => mouseSelector(state).isMouseOnGizmoDown;

export const isPersistingChangesSelector = (state: RootState) => mouseSelector(state).isPersistingChanges;

export const isTransformingCameraSelector = (state: RootState) => mouseSelector(state).isTransformingCamera;

export const isMouseDownSelector = (state: RootState) => mouseSelector(state).isMouseDown;

export const snappingSelector = (state: RootState) => cityViewSettingsSelector(state).snapping;

export const isSnappingActiveSelector = (state: RootState) => cityViewSettingsSelector(state).snapping.active;

export const disableClickInteractionSelector = (state: RootState) =>
  cityViewGeneralSelector(state).disableClickInteraction;

export const solarExposureSettingsSelector = (state: RootState) => cityViewSettingsSelector(state).solarExposure;

export const overrideToolSelector = (state: RootState) => cityViewGeneralSelector(state).overrideTool;

export const terrainSettingsSelector = (state: RootState) => cityViewSettingsSelector(state).terrain;

export const offsetTerrainSelector = (state: RootState) => cityViewSettingsSelector(state).offsetTerrain;

export const isDesktopSelector = (state: RootState) => cityViewGeneralSelector(state).isDesktop;
